import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import {
  DASHBOARD_PRESETS,
  DASHBOARD_THEME_ENTITY,
  DASHBOARD_THEME_KEY,
  DEFAULT_PRESET,
  PresetKey,
  THEMING_ENABLED,
  resolvePresetKey,
} from "../config/dashboardTheme";

/**
 * Tracks the app-wide light/dark mode. App.tsx toggles the `dark` class on
 * <html>, so watch that rather than duplicating the theme state.
 */
const useColorMode = (): "light" | "dark" => {
  const read = (): "light" | "dark" =>
    document.documentElement.classList.contains("dark") ? "dark" : "light";
  const [mode, setMode] = useState<"light" | "dark">(read);

  useEffect(() => {
    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return mode;
};

const readCached = (): PresetKey | null => {
  try {
    const raw = localStorage.getItem(DASHBOARD_THEME_KEY);
    return raw ? resolvePresetKey(JSON.parse(raw)?.preset) : null;
  } catch {
    return null;
  }
};

/**
 * The store's chosen dashboard preset, persisted through the CMS entity (the
 * same mechanism the screen backgrounds use) and cached in localStorage so the
 * dashboard paints with the store's colours before the request resolves.
 *
 * `preview` holds the unsaved selection from the settings drawer so the store
 * sees it live; it is dropped on cancel and committed on save.
 */
export const useDashboardTheme = (userId?: string | number) => {
  const mode = useColorMode();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<PresetKey | null>(null);

  const entityId = `${DASHBOARD_THEME_ENTITY}.${userId}`;

  const { data: rows, isLoading } = useQuery({
    queryKey: ["cms", DASHBOARD_THEME_ENTITY, userId],
    queryFn: () => queryApi(`manage/cms?entity_id=${entityId}`, "GET"),
    enabled: !!userId && THEMING_ENABLED,
    staleTime: Infinity,
    gcTime: 0,
  });

  /** The CMS row backing the choice — updated in place so it stays a single row. */
  const row = Array.isArray(rows) ? rows[0] : undefined;

  const savedKey = useMemo<PresetKey>(() => {
    if (!row?.data) return readCached() ?? DEFAULT_PRESET;
    try {
      return resolvePresetKey(JSON.parse(row.data)?.preset);
    } catch {
      return readCached() ?? DEFAULT_PRESET;
    }
  }, [row?.data]);

  // Keep the local cache in step with the server copy.
  useEffect(() => {
    if (row?.data) localStorage.setItem(DASHBOARD_THEME_KEY, row.data);
  }, [row?.data]);

  const { mutate: persist, isPending: isSaving } = useMutation({
    mutationFn: (key: PresetKey) => {
      const data = JSON.stringify({ preset: key });
      return row?.cms_id
        ? queryApi(`manage/cms/${row.cms_id}`, "PUT", {
            data,
            entity_id: entityId,
          })
        : queryApi(`manage/cms`, "POST", { data, entity_id: entityId });
    },
    onSuccess: () => {
      // The preview is deliberately NOT cleared here. `savedKey` still reads
      // the stale query row until the refetch lands, so dropping the preview
      // now would flash the previous theme across the whole app. The effect
      // below clears it once the server copy agrees.
      queryClient.invalidateQueries({
        queryKey: ["cms", DASHBOARD_THEME_ENTITY, userId],
      });
      triggerNotification({
        title: "Success",
        description: "Dashboard appearance saved",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error?.message || "Failed to save dashboard appearance",
        type: "error",
      });
    },
  });

  // Clear the preview only once the saved value has caught up with it.
  useEffect(() => {
    if (preview && savedKey === preview) setPreview(null);
  }, [preview, savedKey]);

  const save = useCallback(
    /**
     * `options.onSuccess` runs only once the save actually lands, so a caller
     * can close its drawer on success and leave it open (with the error
     * notification) if the request fails.
     */
    (key: PresetKey, options?: { onSuccess?: () => void }) => {
      localStorage.setItem(DASHBOARD_THEME_KEY, JSON.stringify({ preset: key }));
      persist(key, options);
    },
    [persist],
  );

  const reset = useCallback(
    (options?: { onSuccess?: () => void }) => save(DEFAULT_PRESET, options),
    [save],
  );

  /** what the app should render with right now */
  const activeKey = THEMING_ENABLED ? preview ?? savedKey : DEFAULT_PRESET;

  return {
    presetKey: activeKey,
    preset: DASHBOARD_PRESETS[activeKey],
    /** the persisted choice, ignoring an unsaved drawer selection */
    savedKey,
    mode,
    isLoading,
    isSaving,
    /** live-preview a preset without saving it */
    setPreview,
    /** discard the unsaved selection */
    cancelPreview: () => setPreview(null),
    save,
    reset,
  };
};

export default useDashboardTheme;
